import { useTranslation } from "react-i18next";

import { Chip } from "@/components/chip";
import { NARROW_WRAP } from "@/components/data-table";
import { Highlight } from "@/components/highlight";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { isRetiredTool, type ToolTitles } from "@/lib/tool-titles";
import { cn } from "@/lib/utils";

interface ToolNameProps {
  /** The tool's name as Claude calls it, such as `order_locations`. */
  name: string;
  /** Every tool's title (`useToolTitles`), the one rule for where a title comes from. */
  titles: ToolTitles;
  /** A search as typed, marked in the title and the name. */
  search?: string;
}

/**
 * A tool as people read it, wherever a page names one (a call, an issue, a
 * usage row): its title from the catalogue, with its name in small muted mono
 * next to it. A tool the catalogue no longer lists shows its name with a muted
 * "Niet meer beschikbaar" chip, whose tooltip says its earlier calls stay;
 * while the catalogue loads, the name alone. The title takes the weight of
 * where it stands (a table row's title is semibold).
 *
 *     const titles = useToolTitles();
 *     <TableItem title={<ToolName name={row.tool} titles={titles} search={search} />} />
 */
export function ToolName({ name, titles, search = "" }: ToolNameProps) {
  const title = titles.get(name);
  return (
    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      {title && (
        <span>
          <Highlight text={title} query={search} />
        </span>
      )}
      {/* Breaks anywhere, never hyphenated: a hyphen would read as part of the name. */}
      <code
        className={cn(
          "font-mono text-xs font-normal wrap-anywhere hyphens-none",
          title && "text-muted",
        )}
      >
        <Highlight text={name} query={search} />
      </code>
      {isRetiredTool(titles, name) && <RetiredChip />}
    </span>
  );
}

/**
 * "Niet meer beschikbaar", with a tooltip that says what it means. In a
 * table's phone layout it may take two lines, so it never widens the table.
 */
export function RetiredChip() {
  const { t } = useTranslation();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Chip
          tone="neutral"
          tabIndex={0}
          className={cn(
            "focus-visible:ring-ring self-center font-normal focus-visible:ring-2 focus-visible:outline-none",
            NARROW_WRAP,
          )}
        >
          {t("tools.retired.label")}
        </Chip>
      </TooltipTrigger>
      <TooltipContent className="max-w-64 text-left">{t("tools.retired.hint")}</TooltipContent>
    </Tooltip>
  );
}
